import { inject, Injectable } from '@angular/core';
import { assert, isDefined } from '@sama/js-core';
import {
  ContextMenuOption,
  MenuType,
} from '../../team-apis/content/content-connector';
import { shellConnectorToken } from '../../team-apis/core/shell-connector';
import { RedrawService } from '../render/controls/redraw.service';
import { PointCloudContentStore } from '../state/point-cloud-content-store';
import { KdTreeService } from '../utils/kd-tree.service';
import { vectorToPoint } from '../utils/point-cloud-utils';
import { RayCastService } from '../utils/ray-cast.service';
import { CuboidRescaleService } from '../utils/rescale/cuboid-rescale.service';

@Injectable()
export class ContextMenuOptionsService {
  private cuboidRescaleService = inject(CuboidRescaleService);
  private kdTreeService = inject(KdTreeService);
  private rayCastService = inject(RayCastService);
  private redrawService = inject(RedrawService);
  private shellConnector = inject(shellConnectorToken);
  private store = inject(PointCloudContentStore);

  getContextMenuOptions(menuType: MenuType): ContextMenuOption[] {
    if (menuType === 'shape') {
      const isCuboidFocused = isDefined(this.store.getFocusedShapeStore());
      return [
        this.getRescaleOption(),
        isCuboidFocused ? this.getRedrawOption() : undefined,
        this.getAddMissingShapeMarkerOption(),
        this.getAddIssueOption(),
      ].filter(isDefined);
    } else {
      return [this.getAddMissingShapeMarkerOption(), this.getAddIssueOption()];
    }
  }

  private getRescaleOption(): ContextMenuOption {
    return {
      label: 'Rescale',
      // For some reason, we need this void operator to be able to
      // correctly get the dialog harness in tests.
      action: () => void this.rescaleFocusedShape(),
      disabled: false,
      hidden: this.store.state().readOnly,
    };
  }

  private getRedrawOption(): ContextMenuOption {
    return {
      label: 'Redraw',
      // For some reason, we need this void operator to be able to
      // correctly get the dialog harness in tests.
      action: () => void this.redrawService.redraw(),
      disabled: !this.redrawService.canRedraw(),
      hidden: this.store.state().readOnly,
      shortcut: 'redraw',
    };
  }

  private getAddMissingShapeMarkerOption(): ContextMenuOption {
    const imagePoint = this.getImagePoint();
    return {
      label: 'Add missing shape marker',
      hidden: !this.shouldShowAddMissingShapeMarker(),
      action: () => {
        this.shellConnector.addMissingShapeFeedback(imagePoint);
      },
    };
  }

  private getAddIssueOption() {
    const imagePoint = this.getImagePoint();
    return {
      label: 'Add issue',
      hidden: !this.shouldShowAddIssue(),
      action: () => {
        this.shellConnector.addClientFeedback(imagePoint);
      },
    };
  }

  private shouldShowAddMissingShapeMarker() {
    return this.store.state().isShapeFeedbackEditable;
  }

  private rescaleFocusedShape() {
    const focusedCuboidStore$ = this.store.getFocusedShapeStore();
    assert(focusedCuboidStore$, 'a focused shape is required');
    return this.cuboidRescaleService.rescaleWithModal(
      focusedCuboidStore$.state().index,
    );
  }

  private shouldShowAddIssue() {
    return (
      this.store.state().isClientFeedbacksEditable &&
      this.shellConnector.hasPermission('issue_create')
    );
  }

  private getImagePoint() {
    this.rayCastService.updateRay();
    const ray = this.rayCastService.getRay();
    return vectorToPoint(this.kdTreeService.getClosestPoint(ray));
  }
}
